/**
 * AccessibilitySettingsPage - Accessibility Preferences
 * 
 * User preferences for motion, animations, and accessibility features.
 * Uses motionPreferences helper for testable, reusable motion controls.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar, SettingsSection, Toggle } from '../../components';
import { applyReducedMotion, getInitialReducedMotion } from '../../utils/motionPreferences';
import { Accessibility } from 'lucide-react';

export default function AccessibilitySettingsPage() {
  const navigate = useNavigate();
  const [reducedMotion, setReducedMotion] = useState(getInitialReducedMotion);

  // Apply motion preference on mount and when it changes
  useEffect(() => {
    applyReducedMotion(reducedMotion);
  }, [reducedMotion]);

  const handleToggle = (checked: boolean) => {
    setReducedMotion(checked);
    applyReducedMotion(checked);
  };

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Accessibility" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-oro-surface-alt rounded-full flex items-center justify-center">
            <Accessibility className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-text-primary">Accessibility</h1>
            <p className="text-sm text-text-secondary">Adjust motion and animation preferences</p>
          </div>
        </div>

        {/* Motion & Animation Section */}
        <SettingsSection
          title="Motion & Animation"
          description="Control animations and transitions for comfort and accessibility"
        >
          <Toggle
            label="Reduce Motion"
            description="Minimizes animations and transitions throughout the app. Respects your system preferences by default."
            checked={reducedMotion}
            onChange={handleToggle}
          />
        </SettingsSection>

        {/* Info Box */}
        <div className="bg-oro-surface-alt border border-oro-border rounded-xl p-4">
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">About Reduced Motion:</span> When enabled, 
            page transitions, animations, and movement effects are disabled or reduced. This helps users 
            who are sensitive to motion or prefer a calmer interface.
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
